<template>
  <div class="container-fluid py-3">
    <div class="row g-3">
      <!-- Customization Panel -->
      <div class="col-lg-4">
        <div class="card h-100">
          <div class="card-header py-2">
            <span>{{ $t('customization') || 'Customization' }}</span>
          </div>
          <div class="card-body p-2" style="overflow-y: auto; max-height: 70vh;">
            <!-- Printer Size -->
            <div class="mb-3">
              <label class="form-label small fw-bold">{{ $t('printer_size') || 'Printer Size' }}</label>
              <select class="form-select form-select-sm" v-model="printerWidth" @change="updatePreview">
                <option value="280">58mm (280px)</option>
                <option value="380">80mm (380px)</option>
              </select>
            </div>

            <!-- Logo Settings -->
            <div class="mb-3">
              <label class="form-label small fw-bold">{{ $t('logo') || 'Logo' }}</label>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="settings.showLogo" id="showLogo">
                <label class="form-check-label small" for="showLogo">{{ $t('show_logo') || 'Show Logo' }}</label>
              </div>
              <div v-if="settings.showLogo" class="mt-2">
                <small class="text-muted d-block mb-1">Uses business.logo from your business settings</small>
              </div>
            </div>

            <!-- Barcode Settings -->
            <div class="mb-3">
              <label class="form-label small fw-bold">{{ $t('barcode') || 'Barcode' }}</label>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="settings.showBarcode" id="showBarcode">
                <label class="form-check-label small" for="showBarcode">{{ $t('show_barcode') || 'Show Barcode' }}</label>
              </div>
            </div>

            <!-- QR Code Settings -->
            <div class="mb-3">
              <label class="form-label small fw-bold">{{ $t('qr_code') || 'QR Code' }}</label>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="settings.showQrCode" id="showQrCode">
                <label class="form-check-label small" for="showQrCode">{{ $t('show_qr_code') || 'Show QR Code' }}</label>
              </div>
            </div>

            <!-- Footer Message -->
            <div class="mb-3">
              <label class="form-label small fw-bold">{{ $t('footer_message') || 'Footer Message' }}</label>
              <textarea class="form-control form-control-sm" v-model="settings.footerMessage" rows="2"></textarea>
            </div>

            <!-- Additional Receipt Fields -->
            <div class="mb-3">
              <label class="form-label small fw-bold">{{ $t('additional_fields') || 'Additional Fields' }}</label>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="settings.showPaymentMethod" id="showPaymentMethod">
                <label class="form-check-label small" for="showPaymentMethod">{{ $t('payment_method') || 'Payment Method' }}</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="settings.showTaxDetails" id="showTaxDetails">
                <label class="form-check-label small" for="showTaxDetails">{{ $t('tax_details') || 'Tax Details' }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Editor -->
      <div class="col-lg-4" v-show="showEditor">
        <div class="card h-100">
          <div class="card-header py-2 d-flex align-items-center justify-content-between">
            <span>{{ $t('editor') || 'Editor' }}</span>
            <small class="text-muted">{{ $t('use_tokens') || 'Use tokens like business.name, date' }}</small>
          </div>
          <div class="card-body p-2">
            <textarea class="form-control" v-model="templateText" rows="24" @input="updatePreview"></textarea>
          </div>
        </div>
      </div>

      <!-- Preview -->
      <div class="col-lg-4">
        <div class="card h-100">
          <div class="card-header py-2 d-flex align-items-center justify-content-between">
            <span>{{ $t('preview') || 'Preview' }}</span>
            <button class="btn btn-light btn-sm toggle-editor-btn" @click="showEditor = !showEditor" :title="showEditor ? 'Hide editor' : 'Show editor'">
              <AppIcon :name="showEditor ? 'bi bi-eye-slash' : 'bi bi-code-slash'" :class="showEditor ? 'bi bi-eye-slash' : 'bi bi-code-slash'" />
            </button>
          </div>
          <div class="card-body p-3" style="background:#fafafa; overflow-y: auto; max-height: 70vh;">
            <div class="receipt-preview" :style="{width: printerWidth + 'px'}" v-html="renderedHtml"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-3">
        <button class="btn btn-success btn-sm"  style="width:100%" :disabled="saving" @click="saveTemplate">{{ saving ? $t('saving') : $t('save') }}</button>
        <button class="btn btn-outline-secondary btn-sm mt-1" style="width:100%" @click="resetToDefault">{{ $t('reset') || 'Reset' }}</button>
    </div>
  </div>
</template>

<script>
import helper from '../helper'

export default {
  name: 'PosReceiptTemplateBuilder',
  props: {
    sampleData: {
      type: Object,
      default: () => ({})
    }
  },
  data(){
    return {
      templateText: '',
      renderedHtml: '',
      saving: false,
      showEditor: false,
      printerWidth: 280, // Default 58mm printer
      settings: {
        showLogo: true,
        showBarcode: true,
        showQrCode: false,
        footerMessage: '',
        showPaymentMethod: true,
        showTaxDetails: true,
      }
    }
  },
  async mounted(){
    await this.loadExistingTemplate()
    if(!this.templateText){
      this.templateText = this.defaultTemplate()
    }
    this.updatePreview()
  },
  watch: {
    settings: {
      handler(newSettings, oldSettings){
        // Only rebuild template if settings changed
        if(oldSettings){
          this.rebuildTemplateFromSettings()
        }
        this.updatePreview()
      },
      deep: true
    }
  },
  methods: {
    async loadExistingTemplate(){
      try{
        const resp = await helper.customGet(`${process.env.VUE_APP_API}/templates`)
        if(resp?.data?.data?.length){
          const existing = resp.data.data.find(t => (t.type === 'pos_receipt'))
          if(existing && existing.data){
            let templateData = existing.data
            if(typeof templateData === 'string'){
              try {
                templateData = JSON.parse(templateData)
              } catch(e) {
                // If not JSON, treat as plain template text
                this.templateText = templateData
                return
              }
            }
            // Load template and settings
            if(templateData.template){
              this.templateText = templateData.template
            }
            if(templateData.settings){
              this.settings = Object.assign(this.settings, templateData.settings)
            }
            if(templateData.printerWidth){
              this.printerWidth = templateData.printerWidth
            }
          }
        }
      }catch(e){/* ignore */}
    },
    async saveTemplate(){
      try{
        this.saving = true
        const dataToSave = {
          template: this.templateText,
          settings: this.settings,
          printerWidth: this.printerWidth
        }
        await helper.customPost(`${process.env.VUE_APP_API}/templates`,{ 
          type: 'pos_receipt', 
          data: JSON.stringify(dataToSave)
        },{showToast:true})
        this.saving = false
      }catch(e){
        this.saving = false
      }
    },
    resetToDefault(){
      this.templateText = this.defaultTemplate()
      this.settings = {
        showLogo: true,
        showBarcode: true,
        showQrCode: false,
        footerMessage: '',
        showPaymentMethod: true,
        showTaxDetails: true,
      }
      this.printerWidth = 280
      this.updatePreview()
    },
    updatePreview(){
      const data = this.buildSampleData()
      this.renderedHtml = this.renderTemplate(this.templateText, data)
    },
    rebuildTemplateFromSettings(){
      // Rebuild the template based on current settings
      this.templateText = this.generateTemplateFromSettings()
    },
    generateTemplateFromSettings(){
      let template = `<div style="margin:0 auto; font-family: Arial, sans-serif; color:#000; padding:8px;">`
      
      // Logo section
      if(this.settings.showLogo){
        template += `
  {{LOGO}}
  `
      }
      
      // Header section
      template += `
  <div style="text-align:center; margin-bottom:8px">
    <div style="font-weight:bold; font-size:14px">{{business.name}}</div>
    <div style="font-size:11px">{{business.address}}</div>
    <div style="font-size:11px">{{business.phone}}</div>
    <div style="font-size:10px; margin-top:4px">{{date}}</div>
  </div>
  
  <div style="border-top:1px dashed #000; margin:8px 0"></div>
  
  <div style="font-size:10px; margin-bottom:8px">
    <div style="display:flex; justify-content:space-between"><span>Receipt ID:</span><span style="font-weight:bold">{{pos_id}}</span></div>
    <div style="display:flex; justify-content:space-between"><span>{{cashier_label}}:</span><span>{{cashier.name}}</span></div>`
      
      // Payment method
      if(this.settings.showPaymentMethod){
        template += `
    <div style="display:flex; justify-content:space-between"><span>Payment:</span><span>{{payment_method}}</span></div>`
      }
      
      template += `
  </div>
  
  <div style="border-top:1px dashed #000; margin:8px 0"></div>
  
  <table style="width:100%; font-size:10px; border-collapse:collapse">
    <thead>
      <tr style="border-bottom:1px solid #000">
        <th style="text-align:left; padding:4px 0">{{product_label}}</th>
        <th style="text-align:center; padding:4px 0">{{product_quantity}}</th>
        <th style="text-align:right; padding:4px 0">{{price_label}}</th>
        <th style="text-align:right; padding:4px 0">{{total_label}}</th>
      </tr>
    </thead>
    <tbody>
      {{#items}}
      <tr>
        <td style="text-align:left; padding:2px 0">{{name}}</td>
        <td style="text-align:center; padding:2px 0">{{qty}}</td>
        <td style="text-align:right; padding:2px 0">{{price}}</td>
        <td style="text-align:right; padding:2px 0">{{line_total}}</td>
      </tr>
      {{/items}}
    </tbody>
  </table>
  
  <div style="border-top:1px dashed #000; margin:8px 0"></div>
  
  <div style="font-size:11px">
    <div style="display:flex; justify-content:space-between; padding:2px 0"><span>{{subtotal_label}}:</span><span>{{subtotal}}</span></div>
    {{#if_discount}}<div style="display:flex; justify-content:space-between; padding:2px 0"><span>{{discount_label}}:</span><span>{{discount}}</span></div>{{/if_discount}}`
      
      // Tax details
      if(this.settings.showTaxDetails){
        template += `
    <div style="display:flex; justify-content:space-between; padding:2px 0"><span>Tax ({{tax_rate}}):</span><span>{{tax}}</span></div>`
      }
      
      template += `
    <div style="display:flex; justify-content:space-between; font-weight:bold; padding:4px 0; font-size:12px; border-top:1px solid #000; margin-top:4px">
      <span>TOTAL:</span><span>{{total}}</span>
    </div>
  </div>`
      
      // Barcode
      if(this.settings.showBarcode){
        template += `
  
  <div style="margin-top:12px">
    {{BARCODE}}
  </div>`
      }
      
      // QR Code
      if(this.settings.showQrCode){
        template += `
  
  <div style="margin-top:8px">
    {{QRCODE}}
  </div>`
      }
      
      // Footer message
      template += `
  
  <div style="text-align:center; margin-top:12px; font-size:10px; font-style:italic">
    ${this.settings.footerMessage || ''}
  </div>
</div>`
      
      return template
    },
    buildSampleData(){
      const now = new Date()
      const sample = {
        business: {
          name: this.$store.getters.BUSINESS?.name || 'My Business',
          address: this.$store.getters.BUSINESS?.address || '123 Main St',
          phone: this.$store.getters.BUSINESS?.phone || '+212 600 000 000',
          email: this.$store.getters.BUSINESS?.email || 'contact@business.com',
          taxId: this.$store.getters.BUSINESS?.tax_id || 'ICE123456789',
          logo: this.$store.getters.BUSINESS?.logo || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="80" viewBox="0 0 120 80"%3E%3Crect fill="%23e0e0e0" width="120" height="80"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23666"%3ELOGO%3C/text%3E%3C/svg%3E',
        },
        receipt_id: 'TXN-000123',
        pos_id: 'POS-000123',
        cashier: {
          id: 1,
          name: this.$store.getters.USER?.name || 'Sample Cashier'
        },
        cashier_label: this.$t('cashier'),
        product_label: this.$t('product_label'),
        product_quantity: this.$t('product_quantity'),
        price_label: this.$t('product_price'),
        total_label: this.$t('total'),
        subtotal_label: this.$t('subtotal'),
        discount_label: this.$t('discount'),
        date: now.toLocaleString(),
        client: { name: 'Walk-in Client' },
        subtotal: helper.formatMoney(200),
        discount: '10%',
        has_discount: true,
        discount_amount: helper.formatMoney(20),
        tax: helper.formatMoney(18),
        tax_rate: '20%',
        total: helper.formatMoney(208),
        paid_amount: helper.formatMoney(250),
        change_amount: helper.formatMoney(42),
        payment_method: this.$t('cash'),
        items: [
          { name: 'Product A', qty: 1, price: helper.formatMoney(100), line_total: helper.formatMoney(100) },
          { name: 'Product B', qty: 2, price: helper.formatMoney(50), line_total: helper.formatMoney(100) },
        ],
        // Settings for conditional rendering
        settings: this.settings
      }
      return Object.assign(sample, this.sampleData || {})
    },
    // Template renderer with support for variables, items loops, and conditional blocks
    renderTemplate(tpl, data){
      if(!tpl) return ''
      let output = String(tpl)
      
      // Process items loop - matches {{#items}}...{{/items}}
      const loopRegex = /\{\{#items\}\}([\s\S]*?)\{\{\/items\}\}/g
      output = output.replace(loopRegex, (_match, inner) => {
        if(!Array.isArray(data.items)) return ''
        return data.items.map(item => this.replaceTokens(inner, Object.assign({}, data, item))).join('')
      })
      
      // Process discount conditional - matches {{#if_discount}}...{{/if_discount}}
      const discountRegex = /\{\{#if_discount\}\}([\s\S]*?)\{\{\/if_discount\}\}/g
      output = output.replace(discountRegex, (_match, inner) => {
        // Check both has_discount flag and discount value
        if(data.has_discount !== undefined){
          return data.has_discount ? inner : ''
        }
        // Fallback: parse discount value
        const discountValue = parseFloat(String(data.discount || '0').replace(/[^0-9.-]/g, ''))
        return discountValue > 0 ? inner : ''
      })
      
      // Inject special components
      output = this.injectSpecialComponents(output, data)
      
      // Replace simple tokens
      output = this.replaceTokens(output, data)
      
      return output
    },
    replaceTokens(str, data){
      return str.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (m, key) => {
        // Handle special settings tokens
        if(key.startsWith('settings.')){
          const settingKey = key.replace('settings.', '')
          if(data.settings && Object.prototype.hasOwnProperty.call(data.settings, settingKey)){
            return data.settings[settingKey]
          }
          return m
        }
        
        const parts = key.split('.')
        let val = data
        for(const p of parts){
          if(val && Object.prototype.hasOwnProperty.call(val, p)){
            val = val[p]
          }else{
            return m
          }
        }
        return val
      })
    },
    injectSpecialComponents(html, data){
      // Inject Logo - replace all occurrences
      if(data.business && data.business.logo){
        html = html.replace(/\{\{LOGO\}\}/g, `<img src="${data.business.logo}" style="max-width:120px; max-height:80px; display:block; margin:0 auto 8px;">`)
      } else {
        html = html.replace(/\{\{LOGO\}\}/g, '')
      }
      
      // Inject Barcode (using barcode generation)
      const barcodeDataUrl = this.generateBarcode(data.pos_id || data.receipt_id)
      html = html.replace(/\{\{BARCODE\}\}/g, `<img src="${barcodeDataUrl}" style="max-width:100%; height:50px; display:block; margin:8px auto;">`)
      
      // Inject QR Code
      const qrData = `Receipt:${data.pos_id}\nTotal:${data.total}\nDate:${data.date}`
      const qrDataUrl = this.generateQRCode(qrData)
      html = html.replace(/\{\{QRCODE\}\}/g, `<img src="${qrDataUrl}" style="width:100px; height:100px; display:block; margin:8px auto;">`)
      
      return html
    },
    generateBarcode(text){
      // Simple barcode generation using canvas
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = 200
        canvas.height = 50
        
        // Simple Code 128-like pattern (simplified for demo)
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.fillStyle = 'black'
        
        // Draw bars
        let x = 10
        const barWidth = 2
        for(let i = 0; i < text.length; i++){
          const code = text.charCodeAt(i)
          const pattern = code % 2 === 0 ? [1,0,1,0,1] : [1,1,0,0,1]
          pattern.forEach(bar => {
            if(bar === 1){
              ctx.fillRect(x, 5, barWidth, 30)
            }
            x += barWidth
          })
        }
        
        // Draw text below barcode
        ctx.font = '10px monospace'
        ctx.textAlign = 'center'
        ctx.fillText(text, canvas.width / 2, 45)
        
        return canvas.toDataURL()
      } catch(e) {
        return ''
      }
    },
    generateQRCode(text){
      // Simple QR code generation (basic pattern for demo)
      // In production, use a library like qrcode.js
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const size = 100
        const moduleSize = 5
        canvas.width = size
        canvas.height = size
        
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, size, size)
        ctx.fillStyle = 'black'
        
        // Create simple QR-like pattern
        for(let i = 0; i < size; i += moduleSize){
          for(let j = 0; j < size; j += moduleSize){
            // Create pseudo-random pattern based on text
            const hash = (text.charCodeAt((i + j) % text.length) + i + j) % 2
            if(hash === 0){
              ctx.fillRect(i, j, moduleSize, moduleSize)
            }
          }
        }
        
        return canvas.toDataURL()
      } catch(e) {
        return ''
      }
    },
   defaultTemplate() {
      return this.generateTemplateFromSettings()
    }
  }
}
</script>

<style scoped>
.receipt-preview {
  background: white;
  padding: 0;
  border: 1px solid #eee;
  border-radius: 6px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
textarea {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
}
.toggle-editor-btn {
  border: 1px solid #e5e5e5;
}
.form-label {
  margin-bottom: 0.25rem;
}
.card-body::-webkit-scrollbar {
  width: 6px;
}
.card-body::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.card-body::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}
.card-body::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>


